import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { MetricRowData, ScatterSeries } from '../../types';
import { compact, isNil } from 'lodash';
import { formatSubjectWithProvidedFee } from '../../../legend';
import { type BlockAnalysisType, type CustomizableMetric, type TabularDataTypeEnum } from 'venn-utils';
import { type GetRequestSubjectFee, useGetRequestSubjectFee } from '../../../blocks/utils/hooks/use-advisory-fee';
import { dataValueToNumber } from '../../logic/typeUtils';
import type { SubjectPoint } from './chartsUtils';
import { convertScatterSeriesDataToExcel } from './chartsUtils';
import type { Analysis } from 'venn-api';
import {
  blockMetrics,
  blockScatterAxisConfig,
  blockScatterAxisRenderedExtremes,
  blockScatterAxisStaticHints,
  blockSubjectHasProxyState,
  customizedBlock,
  MetricError,
  type ScatterAxisRenderedExtremes,
  type StudioAnalysisRequest,
  type StudioRequestSubject,
  useMetricsErrors,
} from 'venn-state';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useBlockId } from '../../contexts/BlockIdContext';
import {
  useGetBlockSubjectSuperscripts,
  usePageProxySymbol,
} from '../../../blocks/components/core/block-subject-footnotes-and-superscripts';
import { getScatterAxisStaticHints } from '../../../charts/analysis-charts/chart-config-logic';
import { axisFormatFromDataType, chartToDisplay } from '../../../charts/analysis-charts';

export function useScatterChartMetricError() {
  const blockId = useBlockId();
  const block = useRecoilValue(customizedBlock(blockId));
  const selectedMetrics = useRecoilValue(blockMetrics(blockId));
  const { clearMetricByKeys, setMetricErrors } = useMetricsErrors(blockId);

  useEffect(() => {
    const metricKey = 'blockLevel';

    if (block.infoGraphicType === 'SCATTER' && selectedMetrics.length > 2) {
      setMetricErrors([
        {
          metricKey,
          error: MetricError.TWO_METRICS,
        },
      ]);
    }

    return () => clearMetricByKeys([metricKey]);
  }, [block.infoGraphicType, selectedMetrics.length, setMetricErrors, clearMetricByKeys]);
}

export function getScatterMetrics(selectedMetrics: CustomizableMetric[]) {
  /**
   * Export metrics is just the selected metrics, capped to the maximum allowed.
   * Unlike the 2d scatter chart, we don't need to show a duplicate metric if only 1 metric is selected.
   */
  const exportMetrics = selectedMetrics.slice(0, 2);

  /** If only one metric selected, value will be used for both x & y. */
  const chartMetrics = exportMetrics.length === 1 ? [exportMetrics[0]!, exportMetrics[0]!] : exportMetrics;

  return { chartMetrics, exportMetrics };
}

export function getScatterLabels(metrics: CustomizableMetric[] | undefined, isRelativeToBenchmark: boolean) {
  if (!metrics?.length) {
    return { xLabel: '', yLabel: '', xUnit: 'ratio' as const, yUnit: 'ratio' as const };
  }

  const { chartMetrics } = getScatterMetrics(metrics);

  const getLabel = (metricIndex: number) =>
    isRelativeToBenchmark ? chartMetrics[metricIndex]!.relativeLabel : (chartMetrics[metricIndex]!.label ?? '');

  const getUnit = (metricIndex: number) =>
    chartMetrics[metricIndex]?.dataType === 'PERCENTAGE' ? ('percent' as const) : ('ratio' as const);

  const xLabel = getLabel(0);
  const yLabel = getLabel(1);

  const xUnit = getUnit(0);
  const yUnit = getUnit(1);

  return { xUnit, yUnit, xLabel, yLabel, xDataType: chartMetrics[0]?.dataType, yDataType: chartMetrics[1]?.dataType };
}

export function isExportable(
  analysis: (Analysis | undefined)[] | undefined,
  analysisType: BlockAnalysisType | undefined,
) {
  if (!analysisType) return false;
  return !!analysis?.find((a) => a?.analysisType === analysisType)?.exportable?.[0];
}

export function createPointCoordinates(
  requestIndex: number,
  parsedRowData: MetricRowData[],
  analysis: (Analysis | undefined)[] | undefined,
  chartMetrics: CustomizableMetric[],
) {
  return chartMetrics.map((metric: CustomizableMetric) => {
    const rawValue = dataValueToNumber(parsedRowData.find((p) => p.key === metric.key)?.value[requestIndex]);
    return {
      name: metric.label,
      chartValue: rawValue,
      excelValue: isExportable(analysis, metric.analysisType) ? rawValue : undefined,
    };
  });
}

export function convertSubjectPointsToChartPoints(
  subjectPoints: (SubjectPoint & { hasProxy: boolean; proxySymbol: string; isBenchmark: boolean })[],
  getBlockSubjectSuperscripts: ({
    subject,
    isBenchmark,
  }: {
    subject: StudioRequestSubject;
    isBenchmark: boolean;
  }) => string,
  getRequestSubjectFee: GetRequestSubjectFee,
): ScatterSeries {
  return compact(
    subjectPoints.map(({ subject, subjectColor, coordinates, hasProxy, proxySymbol, isBenchmark }) => {
      const dataPoints = coordinates.map((x) => x.chartValue);
      if (dataPoints.some(isNil) || dataPoints.some(Number.isNaN)) {
        return undefined;
      }

      return {
        type: 'scatter' as const,
        name: formatSubjectWithProvidedFee(
          subject,
          proxySymbol,
          hasProxy,
          getBlockSubjectSuperscripts({ subject, isBenchmark: !!subject.individualBenchmark?.benchmark }),
          getRequestSubjectFee?.({ subject, isBenchmark }),
        ),
        data: [dataPoints as [number, number]],
        color: subjectColor,
      };
    }),
  );
}

export function useScatterChartData(
  selectedMetrics: CustomizableMetric[] | undefined,
  rowData: MetricRowData[],
  analyses: (Analysis | undefined)[][] | undefined,
  requests: Pick<StudioAnalysisRequest, 'subject' | 'isBenchmark'>[],
  subjectColors: string[],
) {
  const blockId = useBlockId();
  const subjectHasProxy = useRecoilValue(blockSubjectHasProxyState(blockId));
  const proxySymbol = usePageProxySymbol();
  const getBlockSubjectSuperscripts = useGetBlockSubjectSuperscripts();
  const getRequestSubjectFee = useGetRequestSubjectFee();

  return useMemo(() => {
    if (!rowData.length || selectedMetrics === undefined) {
      return { series: [], excelDataFn: undefined };
    }

    const { chartMetrics, exportMetrics } = getScatterMetrics(selectedMetrics);

    const subjectPoints = requests.map((request, requestIndex) => ({
      subject: request.subject,
      isBenchmark: !!request.isBenchmark,
      subjectColor: subjectColors[requestIndex]!,
      coordinates: createPointCoordinates(requestIndex, rowData, analyses?.[requestIndex], chartMetrics),
      hasProxy: !!subjectHasProxy[request.subject.id],
      proxySymbol,
    }));

    const series = convertSubjectPointsToChartPoints(subjectPoints, getBlockSubjectSuperscripts, getRequestSubjectFee);

    const excelDataFn = () => convertScatterSeriesDataToExcel(subjectPoints, exportMetrics, getRequestSubjectFee);

    return { series, excelDataFn };
  }, [
    analyses,
    getBlockSubjectSuperscripts,
    getRequestSubjectFee,
    proxySymbol,
    requests,
    rowData,
    selectedMetrics,
    subjectColors,
    subjectHasProxy,
  ]);
}

export const useScatterChartAxes = (
  series: ScatterSeries | undefined,
  xDataType: TabularDataTypeEnum | undefined,
  yDataType: TabularDataTypeEnum | undefined,
): ((extremes: { x: ScatterAxisRenderedExtremes; y: ScatterAxisRenderedExtremes }) => void) => {
  const blockId = useBlockId();
  const axisConfig = useRecoilValue(blockScatterAxisConfig(blockId));
  const setStaticHints = useSetRecoilState(blockScatterAxisStaticHints(blockId));
  const setRenderedExtremes = useSetRecoilState(blockScatterAxisRenderedExtremes(blockId));
  const resetStaticHints = useResetRecoilState(blockScatterAxisStaticHints(blockId));
  const resetRenderedExtremes = useResetRecoilState(blockScatterAxisRenderedExtremes(blockId));

  useEffect(
    () => () => {
      resetRenderedExtremes();
      resetStaticHints();
    },
    [resetRenderedExtremes, resetStaticHints],
  );

  const staticHints = useMemo(
    () =>
      !series?.length ? undefined : getScatterAxisStaticHints(series, axisConfig ?? undefined, xDataType, yDataType),
    [series, axisConfig, xDataType, yDataType],
  );

  useEffect(() => {
    if (staticHints) {
      setStaticHints(staticHints);
    }
  }, [staticHints, setStaticHints]);

  const xFormat = useMemo(() => axisFormatFromDataType(xDataType), [xDataType]);
  const yFormat = useMemo(() => axisFormatFromDataType(yDataType), [yDataType]);

  // Highcharts' render event fires on every redraw (hover, resize, etc.); short-circuit here
  // so we don't invoke the Recoil setter when the extremes haven't actually changed.
  const lastExtremesRef = useRef<{
    x: ScatterAxisRenderedExtremes;
    y: ScatterAxisRenderedExtremes;
  } | null>(null);

  return useCallback(
    (extremes: { x: ScatterAxisRenderedExtremes; y: ScatterAxisRenderedExtremes }) => {
      const next = {
        x: {
          min: chartToDisplay(extremes.x.min, xFormat),
          max: chartToDisplay(extremes.x.max, xFormat),
          interval: chartToDisplay(extremes.x.interval, xFormat),
        },
        y: {
          min: chartToDisplay(extremes.y.min, yFormat),
          max: chartToDisplay(extremes.y.max, yFormat),
          interval: chartToDisplay(extremes.y.interval, yFormat),
        },
      };
      const prev = lastExtremesRef.current;
      if (
        prev &&
        prev.x.min === next.x.min &&
        prev.x.max === next.x.max &&
        prev.x.interval === next.x.interval &&
        prev.y.min === next.y.min &&
        prev.y.max === next.y.max &&
        prev.y.interval === next.y.interval
      ) {
        return;
      }
      lastExtremesRef.current = next;
      setRenderedExtremes(next);
    },
    [setRenderedExtremes, xFormat, yFormat],
  );
};
